import React, { useState, useEffect } from "react";
import { HeartIcon, Menu } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import ChallengesSection from "./ChallengesSection";
import DisplayNameCheck from "./DisplayNameCheck";
import FAQ from "./FAQ";
import AdventLeaderboard from "./AdventLeaderboard";
import Authors from "./Authors";
import Story from "./Story";
import { getUser } from "../../features/auth/authSlice";
import { useSelector, useDispatch } from "react-redux";
import "./advent-of-apex-2025.css";
import Footer from "./Footer";
import EmbeddedForm from "./EmbeddedForm";

const Snowflake = ({ style }) => (
  <div className="absolute text-white opacity-70" style={style}>
    ❄
  </div>
);

// Christmas lights component
const ChristmasLights = () => {
  const lights = [];
  const colors = ["red", "green", "yellow", "blue"];

  for (let i = 0; i < 20; i++) {
    const color = colors[i % 4];
    lights.push(
      <div key={i} className={`christmas-light ${color}`}>
        <div className="bulb"></div>
      </div>
    );
  }

  return <div className="christmas-lights-container">{lights}</div>;
};

const AuthorSection = () => (
  <div className="flex flex-col items-center mb-8">
    <div className="flex flex-wrap justify-center gap-4 px-4 text-center">
      {[
        {
          name: "Saman Attar",
          url: "https://www.linkedin.com/in/saman-attar/",
        },
      ].map((author, index) => (
        <a
          key={index}
          href={author.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#FFF9E6] hover:text-[#FFD700] transition-colors duration-300 font-semibold"
        >
          {author.name}
        </a>
      ))}
    </div>
  </div>
);

const CharitySection = () => (
  <div className="relative bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-3xl p-8 mx-4 md:mx-0 border-[5px] border-dashed border-[#FFD700] shadow-2xl">
    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-6xl filter drop-shadow-lg">
      🎄
    </div>
    <div className="flex items-center justify-center space-x-2 mb-4">
      <HeartIcon className="text-[#FFD700]" size={32} />
      <h3 className="text-3xl font-bold text-[#FFFEF7] font-['Cabin_Sketch'] text-shadow-lg">
        Supporting Minnie's Food Pantry
      </h3>
      <HeartIcon className="text-[#FFD700]" size={32} />
    </div>
    <p className="text-[#FFFEF7] mb-6 text-center text-lg font-semibold leading-relaxed">
      Last year we raised $640 - enough for 1,920 meals for those in need.{" "} <br/>
      <br className="hidden md:block" /> Can you help double that impact? These
      challenges are free, but if you have the means, please <br className="hidden md:block" />{" "}
      consider making a donation to help feed families in need. <br/><br/> Even $5 goes a long way - that's 15 meals.
      <br />
    </p>
    <div className="flex flex-wrap justify-center gap-4">
      <a
        href="https://www.gofundme.com/f/camp-apex-minnies-food-pantry"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center px-8 py-4 bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-[#3E2723] text-xl font-extrabold rounded-full transition-all duration-300 border-4 border-[#FFF9C4] shadow-lg hover:shadow-2xl hover:scale-105 hover:-translate-y-1"
      >
        <HeartIcon className="mr-2" size={24} />
        Donate Now <HeartIcon className="mx-2" size={24} /> $1 = 3 Meals 🍽️
      </a>
    </div>
  </div>
);

const HeroSection = () => (
  <section className="relative max-w-7xl mx-auto px-4  pb-20 text-center pt-8">
    <div className="space-y-6 animate-fade-in-up">
      <h1 className="font-['Cabin_Sketch'] text-6xl md:text-8xl text-[#1B5E20] font-bold leading-tight text-shadow-lg">
        Welcome Home,
        <br />
        Little One
      </h1>

      <p className="text-[#3E2723] text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed font-semibold pb-4">
        It's a baby's first Christmas! <br/><br/> Help a new family build the Salesforce automation they need to manage all the tasks and activities for thier new family! <br/><br/> You'll solve 25 daily challenges using Triggers, Flows, Validation Rules, and Apex. Each day tests your
        Salesforce skills along with algorithms, data structures, and API integrations.
      </p>

      {/* <div className="flex justify-center gap-8 flex-wrap mt-16">
        {[
          { number: "25", label: "Days of Challenges" },
          { number: "9 AM", label: "EST Daily Release" },
          { number: "Dec 1-25", label: "Join the Journey" },
        ].map((stat, index) => (
          <div
            key={index}
            className={`bg-white p-8 rounded-2xl shadow-xl border-4 border-[#FFD700] ${
              index === 1 ? "rotate-2" : "-rotate-2"
            } hover:rotate-0 hover:scale-105 transition-all duration-300`}
          >
            <div className="font-['Cabin_Sketch'] text-5xl md:text-6xl text-[#D32F2F] font-bold leading-none text-shadow-stat">
              {stat.number}
            </div>
            <div className="text-[#2E7D32] text-sm md:text-base uppercase tracking-wider font-extrabold mt-2">
              {stat.label}
            </div>
          </div>
        ))}
      </div> */}
    </div>
  </section>
);

const SideNavigation = ({
  sections,
  activeSection,
  isMobile,
  isMenuOpen,
  setIsMenuOpen,
}) => (
  <div className={`${isMobile ? "w-full" : "w-full"} mb-8`}>
    {isMobile && (
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-br from-[#D32F2F] to-[#A91D1D] text-white rounded-xl mb-2 font-bold"
      >
        <span>Menu</span>
        <Menu />
      </button>
    )}
    <div
      className={`flex ${
        isMobile && !isMenuOpen ? "hidden" : "flex"
      } flex-wrap gap-3 justify-center`}
    >
      {sections.map((section) => (
        <Link
          key={section.id}
          to={`/advent/2025/${section.id}`}
          onClick={() => {
            if (isMobile) setIsMenuOpen(false);
          }}
          className={`px-6 py-3 text-lg font-bold rounded-2xl transition-all duration-300 border-4 relative overflow-hidden ${
            activeSection === section.id
              ? "bg-gradient-to-br from-[#D32F2F] to-[#A91D1D] text-white border-[#FFD700] scale-105 shadow-xl"
              : "bg-white text-[#3E2723] border-[#FFD700] hover:scale-105 hover:shadow-lg hover:-rotate-2"
          }`}
        >
          {section.icon} {section.title}
        </Link>
      ))}
    </div>
  </div>
);

const ContentPanel = ({ section, isMobile }) => (
  <div className="w-full bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-5 border-[#D32F2F] min-h-[500px]">
    <div className="max-w-none animate-fade-in">{section?.content}</div>
  </div>
);

const AdventOfApex2025 = () => {
  const dispatch = useDispatch();
  const { tabId } = useParams();
  const activeSection = tabId || "challenges";
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    dispatch(getUser());
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [dispatch]);

  const { user } = useSelector((state) => state.auth);
  const [snowflakes, setSnowflakes] = useState([]);

  useEffect(() => {
    const createSnowflake = () => ({
      id: Math.random(),
      left: `${Math.random() * 100}%`,
      size: Math.random() * 1.5 + 0.8,
      animationDuration: `${Math.random() * 10 + 15}s`,
      animationDelay: `${Math.random() * 5}s`,
    });

    const initialSnowflakes = Array.from({ length: 50 }, createSnowflake);
    setSnowflakes(initialSnowflakes);
  }, []);

  const sections = [
    {
      id: "story",
      title: "Story",
      icon: "📖",
      content: <Story />,
    },
    {
      id: "challenges",
      title: "Challenges",
      icon: "🎄",
      content: (
        <>
          {user && user[0] ? (
            <DisplayNameCheck>
              <ChallengesSection />
            </DisplayNameCheck>
          ) : (
            <ChallengesSection />
          )}
        </>
      ),
    },
    {
      id: "give-back",
      title: "Give Back",
      icon: "❤️",
      content: <CharitySection />,
    },
    {
      id: "authors",
      title: "Authors",
      icon: "⭐",
      content: <Authors />,
    },
    {
      id: "leaderboard",
      title: "Leaderboard",
      icon: "🏆",
      content: <AdventLeaderboard />,
    },
    {
      id: "faq",
      title: "FAQ",
      icon: "❓",
      content: <FAQ />,
    },
    {
      id: "sign-up",
      title: "Get Notified",
      icon: "🔔",
      content: <EmbeddedForm subheading="" formId="f17ce8fd4e" />,
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF9E6] via-[#FFE8CC] to-[#FFDAB9] overflow-hidden relative">
      {/* Snowflakes */}
      {snowflakes.map((snowflake) => (
        <Snowflake
          key={snowflake.id}
          style={{
            left: snowflake.left,
            fontSize: `${snowflake.size}rem`,
            animation: `fall ${snowflake.animationDuration} linear infinite`,
            animationDelay: snowflake.animationDelay,
          }}
        />
      ))}

      {/* Header */}
      <div className="relative">
        <header className="relative !mb-1 bg-gradient-to-b from-[#D32F2F] to-[#A91D1D] border-b-8 border-[#FFD700] shadow-2xl">
          <nav className="max-w-7xl mx-auto px-4 flex justify-between items-center flex-wrap gap-4">
            <div className="text-white text-center text-xl font-extrabold tracking-[0.3em] uppercase">
              Advent of Apex 2025
            </div>
          </nav>
        </header>
        {/* <ChristmasLights /> */}
      </div>
      {/* Christmas Lights */}

      {/* Hero Section */}
      <HeroSection />

      {/* Tabs Section */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <SideNavigation
          sections={sections}
          activeSection={activeSection}
          isMobile={isMobile}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
        />
        <ContentPanel
          section={sections.find((s) => s.id === activeSection)}
          isMobile={isMobile}
        />
      </div>

      {/* Footer */}
      <Footer />

      <style jsx>{`
        @keyframes fall {
          0% {
            transform: translateY(-10vh) rotate(0deg);
            opacity: 0.7;
          }
          100% {
            transform: translateY(110vh) rotate(360deg);
            opacity: 0.7;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out 0.3s both;
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }

        .text-shadow-lg {
          text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.3);
        }

        .text-shadow-stat {
          text-shadow: 3px 3px 0px #ffd700;
        }

        .christmas-lights-container {
          width: 100%;
          height: 120px;
          pointer-events: none;
          z-index: 1000;
          display: flex;
          justify-content: space-around;
        }

        .christmas-light {
          position: relative;
          width: 15px;
          height: 25px;
        }

        .bulb {
          width: 15px;
          height: 25px;
          border-radius: 40% 40% 50% 50%;
          position: relative;
          animation: twinkle 1.5s ease-in-out infinite;
          filter: brightness(1.2);
        }

        .bulb::before {
          content: "";
          position: absolute;
          top: -5px;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: 15px;
          background: #3e2723;
        }

        .christmas-light.red .bulb {
          background: radial-gradient(circle at 30% 30%, #ff6b6b, #d32f2f);
          box-shadow: 0 0 10px #d32f2f, 0 0 20px #d32f2f;
          animation-delay: 0s;
        }

        .christmas-light.green .bulb {
          background: radial-gradient(circle at 30% 30%, #66bb6a, #2e7d32);
          box-shadow: 0 0 10px #2e7d32, 0 0 20px #2e7d32;
          animation-delay: 0.3s;
        }

        .christmas-light.yellow .bulb {
          background: radial-gradient(circle at 30% 30%, #ffee58, #ffd700);
          box-shadow: 0 0 10px #ffd700, 0 0 20px #ffd700;
          animation-delay: 0.6s;
        }

        .christmas-light.blue .bulb {
          background: radial-gradient(circle at 30% 30%, #64b5f6, #1976d2);
          box-shadow: 0 0 10px #1976d2, 0 0 20px #1976d2;
          animation-delay: 0.9s;
        }

        @keyframes twinkle {
          0%,
          100% {
            opacity: 1;
            filter: brightness(1.2);
          }
          50% {
            opacity: 0.4;
            filter: brightness(0.6);
          }
        }

        .drop-shadow-glow {
          filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.6));
        }
      `}</style>
    </div>
  );
};

export default AdventOfApex2025;
